import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useDeleteProjectGroupMutation } from '@/redux/projectGroup/projectGroup.api.slice';
import { STEP_LABELS } from './stepLabels';
import {
  selectV2,
  setChildIds,
  setGroupId,
  setMode,
  setMultiStage,
  setStep,
  openWorkpiece,
} from './v2Upload.slice';

const SIGNOFF_STEP = STEP_LABELS.length - 1;

/**
 * Abandons a multi-workpiece project that turned out to be one board, keeping
 * the workpiece as an ordinary single project.
 *
 * Dropping the container also detaches the child, without which the group's
 * admin-list filter would hide the submission from every reviewer.
 */
export default function useConvertToSingle() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { groupId, postId } = useSelector(selectV2);
  const [deleteGroup] = useDeleteProjectGroupMutation();

  // fallow-ignore-next-line complexity
  return async function convertToSingle(target: string | null) {
    if (!target) {
      toast.error('Nothing to submit yet', {
        description: 'Finish this workpiece first.',
      });
      return;
    }
    try {
      if (groupId) await deleteGroup(groupId).unwrap();
    } catch {
      toast.error('Could not switch to a single project', {
        description: 'Please try again.',
      });
      return;
    }

    // Navigating between two routes that render the same element does NOT
    // remount, so every part of the switch is dispatched by hand.
    if (target === postId) {
      // Already the loaded workpiece, so its sign-off can be opened directly.
      dispatch(setMultiStage('workpiece'));
      dispatch(setStep(SIGNOFF_STEP));
    } else {
      dispatch(openWorkpiece({ postId: target, index: 1 }));
    }
    dispatch(setMode('single'));
    dispatch(setGroupId(null));
    dispatch(setChildIds([]));
    navigate(`/project/v2/edit/${target}`, { replace: true });
  };
}
